import React, { useEffect, useState } from 'react';
import { MailWarning, Loader2 } from 'lucide-react';

// Per-tenant security-email config for embed-origin changes. Reads/writes
// GET|PUT /api/mgmt/v1/admin/embed-alerts using the pasted mgmt key or the
// signed-in admin session. Delivery uses the tenant's configured email
// provider (Resend/SendGrid) set in Administration.
export default function EmbedAlertsManager({ apiBase, bearerKey }) {
  const [expanded, setExpanded] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [contentEnabled, setContentEnabled] = useState(false);
  const [securityEnabled, setSecurityEnabled] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const authOpts = () => {
    const key = bearerKey?.trim();
    return { headers: key ? { Authorization: `Bearer ${key}` } : {}, credentials: key ? 'same-origin' : 'include' };
  };

  const load = async () => {
    setLoading(true); setErr(''); setMsg('');
    try {
      const res = await fetch(`${apiBase}/api/mgmt/v1/admin/embed-alerts`, authOpts());
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setEnabled(!!data.enabled);
      setContentEnabled(!!data.content_enabled);
      setSecurityEnabled(!!data.security_enabled);
      setRecipients((data.recipients || []).join(', '));
    } catch (e) { setErr(String(e.message || e)); } finally { setLoading(false); }
  };

  useEffect(() => { if (expanded) load(); }, [expanded]);

  const save = async () => {
    setLoading(true); setErr(''); setMsg('');
    try {
      const o = authOpts();
      const res = await fetch(`${apiBase}/api/mgmt/v1/admin/embed-alerts`, {
        method: 'PUT',
        headers: { ...o.headers, 'Content-Type': 'application/json' },
        credentials: o.credentials,
        body: JSON.stringify({ enabled, content_enabled: contentEnabled, security_enabled: securityEnabled, recipients }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setRecipients((data.recipients || []).join(', '));
      setMsg('Saved.');
    } catch (e) { setErr(String(e.message || e)); } finally { setLoading(false); }
  };

  return (
    <div className="rounded-lg border border-border/40 bg-muted/10 mb-6" data-testid="embed-alerts-manager">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="embed-alerts-toggle"
      >
        <div className="flex items-center gap-2">
          <MailWarning className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium">Email alerts on origin changes</span>
          <span className="text-[10px] text-muted-foreground">notify security by email</span>
        </div>
        <span className="text-[10px] text-muted-foreground">{expanded ? 'Hide' : 'Show'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border/40 pt-3">
          <label className="flex items-center gap-2 text-[12px]">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="embed-alerts-enabled" />
            Send an email whenever a workspace&rsquo;s embed origins change
          </label>
          <label className="flex items-center gap-2 text-[12px]">
            <input type="checkbox" checked={contentEnabled} onChange={(e) => setContentEnabled(e.target.checked)} data-testid="embed-alerts-content-enabled" />
            Send an email whenever a partner creates or deletes a report/dashboard
          </label>
          <label className="flex items-center gap-2 text-[12px]">
            <input type="checkbox" checked={securityEnabled} onChange={(e) => setSecurityEnabled(e.target.checked)} data-testid="embed-alerts-security-enabled" />
            Send an email on security events (new-location admin sign-in, account lockout)
          </label>
          <div>
            <div className="text-[10px] text-muted-foreground mb-1">Recipients (comma-separated)</div>
            <input
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="security@acme.com, oncall@acme.com"
              spellCheck={false}
              className="w-full font-mono text-[11px] px-2 py-1.5 rounded border border-border/40 bg-background/60"
              data-testid="embed-alerts-recipients"
            />
          </div>
          <p className="text-[10px] text-muted-foreground">
            Uses the email provider configured in Administration for this tenant. If none is set, alerts are logged but not delivered.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={loading}
              className="text-[11px] px-3 py-1.5 rounded border border-primary/50 bg-primary/10 text-primary disabled:opacity-40"
              data-testid="embed-alerts-save"
            >
              Save
            </button>
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
            {msg && <span className="text-[10px] text-emerald-300" data-testid="embed-alerts-msg">{msg}</span>}
            {err && <span className="text-[10px] text-rose-400" data-testid="embed-alerts-error">{err}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
